import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";
import CartaRemisoriaTrazabilidadVista from "./CartaRemisoriaTrazabilidadVista";
import AsignacionInterventorPanel from "./AsignacionInterventorPanel";

const getToken = () => {
  const encoded = localStorage.getItem("authToken");
  return encoded ? atob(encoded) : "";
};

const getUsuarioSesion = () => {
  try {
    const u = localStorage.getItem("user");
    return u ? atob(u) : "sistema";
  } catch {
    return "sistema";
  }
};

type DocCartaJefe = {
  tipo: string;
  etiqueta: string;
  id: number;
  nombre_original: string;
  created_at: string | null;
};

type Pendiente = {
  proyecto_id: number;
  codigo: string;
  nombre: string;
  estado_global: string | null;
  documento_cargado_at: string | null;
  documentos: DocCartaJefe[];
};

type TrazFila = {
  proyecto_id: number;
  codigo: string;
  nombre: string;
  estado_global: string | null;
  carta_remisoria_jefe: Record<string, any>;
  documentos: DocCartaJefe[];
  revision_updated_at: string | null;
};

function DocumentosCartaJefeVista({
  documentos,
  abriendoId,
  onVer,
}: {
  documentos: DocCartaJefe[];
  abriendoId: number | null;
  onVer: (docId: number) => void;
}) {
  if (!documentos?.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        Sin documentos cargados
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5}>
      {documentos.map((doc) => (
        <Stack key={doc.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="caption">{doc.etiqueta}:</Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={abriendoId === doc.id}
            onClick={() => onVer(doc.id)}
          >
            {abriendoId === doc.id ? "Abriendo…" : "Ver documento"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {doc.nombre_original}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export default function CartaRemisoriaJefeAprobacionesPanel() {
  const navigate = useNavigate();
  const baseUrl = getProyectosApiBaseUrl();
  const token = React.useMemo(() => getToken(), []);

  const [mainTab, setMainTab] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [loadingTraz, setLoadingTraz] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<Pendiente[]>([]);
  const [trazItems, setTrazItems] = React.useState<TrazFila[]>([]);
  const [comentarios, setComentarios] = React.useState<Record<number, string>>({});
  const [resolviendo, setResolviendo] = React.useState<number | null>(null);
  const [abriendoDoc, setAbriendoDoc] = React.useState<number | null>(null);

  const abrirDocumento = async (docId: number) => {
    try {
      setAbriendoDoc(docId);
      setError(null);
      const res = await fetch(`${baseUrl}/documentos/${docId}/archivo`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No se pudo abrir el documento.");
      const mimeType = data?.mimeType || "application/pdf";
      const url = `data:${mimeType};base64,${data?.contentBase64 || ""}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      setError(e.message || "Error al abrir documento.");
    } finally {
      setAbriendoDoc(null);
    }
  };

  const cargarPendientes = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${baseUrl}/cartas-remisoria-jefe/pendientes`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No fue posible cargar las solicitudes.");
      setItems(await res.json());
    } catch (e: any) {
      setError(e.message || "Error al cargar.");
    } finally {
      setLoading(false);
    }
  }, [baseUrl, token]);

  const cargarTrazabilidad = React.useCallback(async () => {
    try {
      setLoadingTraz(true);
      setError(null);
      const res = await fetch(`${baseUrl}/cartas-remisoria-jefe/trazabilidad`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No fue posible cargar la trazabilidad.");
      setTrazItems(await res.json());
    } catch (e: any) {
      setError(e.message || "Error al cargar trazabilidad.");
    } finally {
      setLoadingTraz(false);
    }
  }, [baseUrl, token]);

  React.useEffect(() => {
    cargarPendientes();
  }, [cargarPendientes]);

  React.useEffect(() => {
    if (mainTab === 2) cargarTrazabilidad();
  }, [mainTab, cargarTrazabilidad]);

  const resolver = async (id: number, decision: "aprobado" | "rechazado") => {
    try {
      setResolviendo(id);
      setError(null);
      const res = await fetch(`${baseUrl}/${id}/cartas-remisoria-jefe/resolucion`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          decision,
          comentario_jefe: comentarios[id] || "",
          updated_by: getUsuarioSesion(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "No fue posible registrar la resolución.");
      }
      await cargarPendientes();
      if (mainTab === 2) await cargarTrazabilidad();
    } catch (e: any) {
      setError(e.message || "Error al resolver.");
    } finally {
      setResolviendo(null);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Asignacion interventor
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Revise la <strong>carta remisoria</strong> y el <strong>comprobante de pago diseño</strong> cargados por el
        solicitante antes de continuar con la asignación del interventor.
      </Typography>

      <Tabs
        value={mainTab}
        onChange={(_, v) => setMainTab(v)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        <Tab label="Pendientes de aprobación" />
        <Tab label="Asignación de interventor" />
        <Tab label="Trazabilidad por proyecto" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mainTab === 0 && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button variant="outlined" onClick={cargarPendientes} disabled={loading}>
              Refrescar
            </Button>
          </Stack>
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="body2">Cargando…</Typography>
            </Stack>
          ) : items.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography color="text.secondary">
                No hay carta remisoria ni comprobante de pago pendientes de su revisión.
              </Typography>
            </Paper>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Proyecto</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Estado global</TableCell>
                  <TableCell>Documentos</TableCell>
                  <TableCell>Fecha carga completa</TableCell>
                  <TableCell>Comentario jefe (opcional)</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.proyecto_id}>
                    <TableCell>{row.nombre}</TableCell>
                    <TableCell>{row.codigo}</TableCell>
                    <TableCell>{row.estado_global || "—"}</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <DocumentosCartaJefeVista
                        documentos={row.documentos || []}
                        abriendoId={abriendoDoc}
                        onVer={abrirDocumento}
                      />
                    </TableCell>
                    <TableCell>
                      {row.documento_cargado_at ? new Date(row.documento_cargado_at).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Observación al solicitante o interna"
                        value={comentarios[row.proyecto_id] || ""}
                        onChange={(e) =>
                          setComentarios((prev) => ({ ...prev, [row.proyecto_id]: e.target.value }))
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="column" spacing={0.5} alignItems="flex-end">
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={resolviendo === row.proyecto_id}
                          onClick={() => resolver(row.proyecto_id, "aprobado")}
                        >
                          Aprobar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={resolviendo === row.proyecto_id}
                          onClick={() => resolver(row.proyecto_id, "rechazado")}
                        >
                          Rechazar
                        </Button>
                        <Button size="small" onClick={() => navigate(`/proyectos-panel/${row.proyecto_id}/gestion`)}>
                          Abrir gestión
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      {mainTab === 1 && <AsignacionInterventorPanel />}

      {mainTab === 2 && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button variant="outlined" onClick={cargarTrazabilidad} disabled={loadingTraz}>
              Refrescar trazabilidad
            </Button>
          </Stack>
          {loadingTraz ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="body2">Cargando trazabilidad…</Typography>
            </Stack>
          ) : trazItems.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography color="text.secondary">
                No hay proyectos con trazabilidad de carta remisoria registrada.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1}>
              {trazItems.map((row) => (
                <Accordion key={row.proyecto_id} defaultExpanded={false} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ pr: 1 }}>
                      <Typography fontWeight={800}>{row.codigo}</Typography>
                      <Typography color="text.secondary">{row.nombre}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Última actualización checklist:{" "}
                        {row.revision_updated_at
                          ? new Date(row.revision_updated_at).toLocaleString("es-CO")
                          : "—"}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    {(row.documentos || []).length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                          Documentos cargados
                        </Typography>
                        <DocumentosCartaJefeVista
                          documentos={row.documentos}
                          abriendoId={abriendoDoc}
                          onVer={abrirDocumento}
                        />
                      </Box>
                    )}
                    <CartaRemisoriaTrazabilidadVista data={row.carta_remisoria_jefe} />
                    <Button
                      size="small"
                      sx={{ mt: 2 }}
                      onClick={() => navigate(`/proyectos-panel/${row.proyecto_id}/gestion`)}
                    >
                      Abrir gestión del proyecto
                    </Button>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}
